import React from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { publicRoutes } from "router/routes"

import * as BikeApi from "features/bike/api/BikeApi"
import { ErrorResponse } from "models"
import { useForm, useAlert, useOperator } from "hooks"

import PublicLayout from "components/Layout/PublicLayout"
import { LeftWave, RightWave, Button, Spinner, Markdown } from "components/Elements"
import { formStatusEnum, FormElement } from "components/Form"
import { Seo } from "components/Seo"
import { isNotEmpty } from "utils"
import { BICYCODE_ID } from "operators/bicycode/config"

type BikePortabilityProps = Record<string, never>

const REQUEST_ACCEPTED = "accepted"
const REQUEST_REFUSED = "rejected"

export const BikePortabilityPage: React.FC<BikePortabilityProps> = () => {
	const { formFields, setFormFields, formStatus, setFormStatus } = useForm({
		defaultFormFields: null,
		defaultFormStatus: formStatusEnum.loading,
	})

	const isReady = formStatus === formStatusEnum.ready
	const isLoading = formStatus === formStatusEnum.loading
	const isError = formStatus === formStatusEnum.errored
	const isExpired = formStatus === formStatusEnum.expired

	const { t } = useTranslation()
	const navigate = useNavigate()
	const { alert } = useAlert()
	const location = useLocation()
	const { currentOperator } = useOperator()
	const [transferCode, setTransferCode] = React.useState<string>(null)

	// Handle fetch bike
	// -------------------------------
	const handleBikeFetch = async (bikeId: string, transferCode: string) => {
		try {
			const operator = currentOperator?.id
			const { res: fetchedBike, getError: getBikeError } =
				await BikeApi.getBikePortabilityInfo(bikeId, transferCode, operator)
			if (getBikeError() || !fetchedBike) throw getBikeError()

			setFormFields(fetchedBike)
			setFormStatus(formStatusEnum.ready)
		} catch (error) {
			const errorMsg = (error as ErrorResponse)?.message?.toString()

			const isRequestExpired = (error as ErrorResponse)?.code === "FnuciApiException"
			if (isRequestExpired) setFormStatus(formStatusEnum.expired)

			if (errorMsg && !isRequestExpired) alert("error", errorMsg)
			setFormStatus(formStatusEnum.errored)
		}
	}

	React.useEffect(() => {
		const urlParams = new URLSearchParams(location?.search)
		const bikeId = urlParams.get("bike_id")
		const transferCode = urlParams.get("transfer_code")

		// Added timeout to avoid issues with HTTP instance in BikeApi with Axios get
		if (isNotEmpty(bikeId) && isNotEmpty(transferCode) && isNotEmpty(currentOperator?.id)) {
			setTimeout(() => handleBikeFetch(bikeId, transferCode), 200)
			setTransferCode(transferCode)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, currentOperator?.id])

	const handleSubmit = async (userChoice: string) => {
		try {
			// prevent form to be submitted again
			setFormStatus(formStatusEnum.loading)
			const updatedRequest = {
				bike_id: formFields.bike_id,
				operator: currentOperator?.id,
				transfer_code: transferCode,
				response: userChoice,
			}
			const { res: confirmUpdateBikeNotification, getError } =
				await BikeApi.updatePortabilityUserResponse(updatedRequest)
			if (getError()) throw getError()
			if (confirmUpdateBikeNotification) {
				if (userChoice === REQUEST_ACCEPTED) {
					alert("success", t("portability.request-accepted", { ns: "bike" }))
				} else {
					alert("success", t("portability.request-refused", { ns: "bike" }))
				}
			}
			navigate(publicRoutes.signin.path)
			setFormStatus(formStatusEnum.expired)
		} catch (error) {
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
			setFormStatus(formStatusEnum.ready)
		}
	}

	const mainUrl =
		process.env.REACT_APP_MODE === "production"
			? `${window.location.origin}`
			: `${process.env.PUBLIC_URL}`

	return (
		<PublicLayout>
			<div className="max-w-screen-3xl mx-auto flex flex-col flex-1 justify-center">
				<Seo
					title={t("propertyRequestTransfer_title", {
						ns: "seo",
						operator: currentOperator?.name,
					})}
				/>
				<LeftWave className="fixed bottom-0 left-0" />
				<RightWave className="fixed bottom-0 right-0" />

				{currentOperator?.id === BICYCODE_ID && (
					<img
						src={`${mainUrl}/images/Chevalier_2.png`}
						alt=""
						width="460"
						height="460"
						className="hidden lg:block fixed bottom-2 right-0"
					/>
				)}

				<div className="text-center flex flex-col justify-center relative py-16">
					<h1 className="c-h1 text-primary mb-8">
						{t("portability.title", {
							ns: "bike",
						})}
					</h1>
					{!isExpired && isReady && (
						<p className="mb-6 max-w-screen-md">
							<Markdown
								specificComponents={{
									p: ({ children }: Record<any, any>) => <p>{children}</p>,
								}}
							>
								{t("portability.description", {
									ns: "bike",
									store: formFields?.request_data?.social_reason,
								})}
							</Markdown>
						</p>
					)}

					{formFields && (
						<h2 className="c-h2 text-primary mb-8">
							{formFields.request_data.social_reason},{" "}
							{formFields.request_data.store_city}
						</h2>
					)}

					{!isExpired && isReady && (
						<>
							<form className="flex-col flex justify-center items-center">
								<FormElement
									value={formFields.bike_description.brand}
									name="brand"
									type="text"
									label={t("description.brand", { ns: "bike" })}
									className="my-1 md:w-4/12 w-full"
									disabled={true}
								/>

								<FormElement
									value={formFields.bike_description.model}
									name="model"
									type="text"
									label={t("description.model", { ns: "bike" })}
									className="my-1 md:w-4/12 w-full"
									disabled={true}
								/>

								<FormElement
									value={formFields.bike_description.gear_type}
									name="gear_type"
									type="text"
									label={t("description.type", { ns: "bike" })}
									className="my-1 md:w-4/12 w-full"
									disabled={true}
								/>

								<FormElement
									value={formFields.bike_description.color}
									name="color"
									type="text"
									label={t("description.color", { ns: "bike" })}
									className="my-1 md:w-4/12 w-full"
									disabled={true}
								/>

								<FormElement
									value={formFields.bike_id}
									name="bike_id"
									type="text"
									label={t("dashboard.register_code", { ns: "bike" })}
									className="my-1 md:w-4/12 w-full"
									disabled={true}
								/>

								<div className="flex items-center mt-12 justify-center md:justify-start">
									<Button
										onClick={() => handleSubmit(REQUEST_ACCEPTED)}
										className="mx-2 md:px-12 px-4 py-1"
										variant="secondary"
									>
										{t("portability.accept", {
											ns: "bike",
										})}
									</Button>
									<Button
										onClick={() => handleSubmit(REQUEST_REFUSED)}
										className="mx-2 px-8 py-1 "
										variant="secondaryBordered"
									>
										{t("portability.refuse", {
											ns: "bike",
										})}
									</Button>
								</div>
							</form>
						</>
					)}

					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}

					{isError && (
						<div className="flex justify-center my-8 text-m text-red-500">
							{t("error_bike_no_info", { ns: "form" })}
						</div>
					)}

					{isExpired && (
						<>
							<div className="flex justify-center my-8">
								{t("transfer_request.request-expired", {
									ns: "bike",
								})}
							</div>
							<div className="flex justify-center mt-4">
								<Button link={publicRoutes.signin.path} variant="secondaryBordered">
									{t("transfer_request.go-home", { ns: "bike" })}
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</PublicLayout>
	)
}
